/**
 * Kleine Markierungen für Listeneinträge der Verwaltung: Initialen, Fortschritt, Hinweis. Wie bei der Pille gilt:
 * **Farbe trägt nie allein die Aussage** — Balken und Punkt stehen immer neben einem Wort oder einer Zahl.
 */

import type { CSSProperties, ReactNode } from 'react';

import { classNames } from './classNames';
import { initialen } from './initialen';

export interface AvatarProps {
  name: string;
  /** 44 statt 32 px, z. B. im Seitenkopf. */
  gross?: boolean;
}

/** Initialenkreis (aus Axis). Rein schmückend: Der Name steht immer daneben. */
export function Avatar({ name, gross }: AvatarProps) {
  return (
    <span className={classNames('ksb-avatar', gross && 'ksb-avatar--lg')} aria-hidden="true">
      {initialen(name)}
    </span>
  );
}

export interface FortschrittProps {
  wert: number;
  von: number;
  /** Text neben dem Balken; Standard „wert von von“. */
  text?: ReactNode;
}

/** Schmaler Balken mit Zahl daneben. Der Balken ist schmückend, die Zahl trägt die Aussage. */
export function Fortschritt({ wert, von, text }: FortschrittProps) {
  const anteil = von > 0 ? Math.min(1, Math.max(0, wert / von)) : 0;
  return (
    <span className={classNames('ksb-meter', anteil === 1 && 'ksb-meter--voll')}>
      <span className="ksb-meter__bahn" aria-hidden="true">
        <span className="ksb-meter__wert" style={{ inlineSize: `${anteil * 100}%` }} />
      </span>
      <span className="ksb-tnum">{text ?? `${wert} von ${von}`}</span>
    </span>
  );
}

export interface MarkerProps {
  /** Farbe des Punkts vor dem Wort — ein Token (`var(--color-…)`). */
  dot?: string;
  /** Ein Symbol statt des Punkts. */
  icon?: ReactNode;
  /** Symbol auf gelber Scheibe (Warnung). */
  warnung?: boolean;
  children: ReactNode;
}

/** Hinweis in kleiner Schrift mit Punkt oder Symbol — leiser als eine Pille, mehrere dürfen untereinander stehen. */
export function Marker({ dot, icon, warnung, children }: MarkerProps) {
  return (
    <span className="ksb-marker">
      {icon && (warnung ? <span className="ksb-flag-warn">{icon}</span> : icon)}
      {dot !== undefined && (
        <span className="ksb-dot" style={{ '--c': dot } as CSSProperties} aria-hidden="true" />
      )}
      {children}
    </span>
  );
}
